import type { Signal } from '@preact/signals-react'
import { signal } from '@preact/signals-react'
import type { Option } from '@susu/headless-commerce/features/configurator/domain/Option/Option'
import { isString } from '@susu/string'
import { isUndefined } from '@susu/undefined'
import type { Configurator } from '../Configurator/Configurator'
import type { ProductIdFilter } from '../Filter/ProductIdFilter'
import type { previewCloudinaryConfigurationType } from './StepDataAccessObject'

export type StepImageType = 'layered-image' | 'static-image'

export class Step {
  allOptions: Array<Option>
  configurator: Signal<Configurator>
  imageType: Signal<StepImageType>
  name: Signal<string>
  previewCloudinaryConfiguration: Signal<previewCloudinaryConfigurationType>
  previewImageFocalPoint: Signal<string | undefined>
  previewImageFocalPointMobile: Signal<string | undefined>
  previewImageFocalPointTablet: Signal<string | undefined>
  previewImageZoom: Signal<string | undefined>
  previewImageZoomMobile: Signal<string | undefined>
  previewImageZoomTablet: Signal<string | undefined>
  staticImagePatternDesktop: Signal<string | undefined>
  staticImagePatternMobile: Signal<string | undefined>
  title: Signal<string>
  visibility: Signal<Array<ProductIdFilter>>

  constructor(
    configurator: Configurator,
    name: string,
    options: Array<Option>,
    previewImageFocalPoint: string | undefined,
    previewImageFocalPointMobile: string | undefined,
    previewImageFocalPointTablet: string | undefined,
    previewImageZoom: string | undefined,
    previewImageZoomMobile: string | undefined,
    previewImageZoomTablet: string | undefined,
    staticImagePatternDesktop: string | undefined,
    staticImagePatternMobile: string | undefined,
    title: string,
    visibility: Array<ProductIdFilter>,
    previewCloudinaryConfiguration: previewCloudinaryConfigurationType,
  ) {
    this.allOptions = options
    this.configurator = signal(configurator)
    this.imageType = signal(
      isString(staticImagePatternMobile) && isString(staticImagePatternDesktop)
        ? 'static-image'
        : 'layered-image',
    )
    this.name = signal(name)
    this.previewCloudinaryConfiguration = signal(previewCloudinaryConfiguration)
    this.previewImageFocalPoint = signal(previewImageFocalPoint)
    this.previewImageFocalPointMobile = signal(previewImageFocalPointMobile)
    this.previewImageFocalPointTablet = signal(previewImageFocalPointTablet)
    this.previewImageZoom = signal(previewImageZoom)
    this.previewImageZoomMobile = signal(previewImageZoomMobile)
    this.previewImageZoomTablet = signal(previewImageZoomTablet)
    this.staticImagePatternDesktop = signal(staticImagePatternDesktop)
    this.staticImagePatternMobile = signal(staticImagePatternMobile)
    this.title = signal(title)
    this.visibility = signal(visibility)
  }

  // Filters the options by visibility.
  get options(): Signal<Array<Option>> {
    const filteredOptions: Array<Option> = []
    for (const option of this.allOptions) {
      if (option.isVisible()) {
        filteredOptions.push(option)
      }
    }
    return signal(filteredOptions)
  }

  set options(options: Option[]) {
    this.allOptions = options
  }

  isLayered() {
    return this.imageType.value === 'layered-image'
  }

  isStatic() {
    return this.imageType.value === 'static-image'
  }

  isOutOfStock() {
    return this.options.value.every(
      (option) =>
        option.product.value.availability.value.isAvailable.value === false,
    )
  }

  getFirstInStockOption() {
    for (const option of this.options.value) {
      if (option.product.value.availability.value.isAvailable.value) {
        return option
      }
    }
  }

  isVisible() {
    if (this.visibility.value.length === 0) {
      return true
    }

    return this.visibility.value.some((filter) =>
      filter.isTrue(this.configurator.value),
    )
  }

  isHidden() {
    return !this.isVisible()
  }

  getProductIds() {
    return this.options.value.map((option) => option.product.value.id.value)
  }

  findOptionByProductId(productId: string | null) {
    for (const option of this.options.value) {
      if (option.product.value.id.value === productId) {
        return option
      }
    }
  }

  getCampaignIds() {
    const campaignIds: string[] = []
    for (const option of this.allOptions) {
      if (isUndefined(option.campaignId.value)) {
        continue
      }
      campaignIds.push(option.campaignId.value)
    }
    return campaignIds
  }
}
